import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from "../../../config";

const Expenses = () => {
  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseFormData, setExpenseFormData] = useState({
    categoryId: '',
    amount: '',
    bankAccountId: '',
    paymentMethod: ''
  });

  const [expenseLoading, setExpenseLoading] = useState(false);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem("token");
      
      const [catRes, bankRes, expRes] = await Promise.all([
        axios.get(`${API_URL}/api/expense-category`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/bank-account`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/expense`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (catRes.data.success) setCategories(catRes.data.categories);
      if (bankRes.data.success) setBankAccounts(bankRes.data.accounts);
      if (expRes.data.success) setExpenses(expRes.data.expenses);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleExpenseChange = (e) => {
    setExpenseFormData({ ...expenseFormData, [e.target.name]: e.target.value });
  };

  const submitExpense = async (e) => {
    e.preventDefault();
    setExpenseLoading(true);
    try {
      const token = localStorage.getItem("token");
      let res;
      if (editingExpenseId) {
        res = await axios.put(`${API_URL}/api/expense/${editingExpenseId}`, expenseFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        res = await axios.post(`${API_URL}/api/expense`, expenseFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      if (res.data.success) {
        alert(res.data.message);
        setExpenseFormData({ categoryId: '', amount: '', bankAccountId: '', paymentMethod: '' });
        setEditingExpenseId(null);
        setShowExpenseForm(false);
        fetchAllData();
      } else {
        alert(res.data.message);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error processing request');
    }
    setExpenseLoading(false);
  };

  const handleEdit = (expense) => {
    setExpenseFormData({
      categoryId: expense.categoryId?._id || '',
      amount: expense.amount,
      bankAccountId: expense.bankAccountId?._id || '',
      paymentMethod: expense.paymentMethod || ''
    });
    setEditingExpenseId(expense._id);
    setShowExpenseForm(true);
  };

  const deleteExpense = async (id) => {
    if (window.confirm("Are you sure you want to delete this expense? (This will refund the amount to the bank account)")) {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.delete(`${API_URL}/api/expense/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          alert(res.data.message);
          fetchAllData();
        }
      } catch (error) {
        alert('Error deleting expense');
      }
    }
  };

  const calculateTotal = () => {
    return expenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Expenses Log</h2>
        <button 
          onClick={() => {
            if (showExpenseForm) {
              setExpenseFormData({ categoryId: '', amount: '', bankAccountId: '', paymentMethod: '' });
              setEditingExpenseId(null);
            }
            setShowExpenseForm(!showExpenseForm);
          }}
          style={{ 
            padding: '10px 20px', 
            backgroundColor: showExpenseForm ? '#6c757d' : '#28a745', 
            color: '#fff', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          {showExpenseForm ? 'Cancel' : '+ Add Expense'}
        </button>
      </div>

      {showExpenseForm && (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '30px', borderLeft: '4px solid #28a745' }}>
          <h4 style={{ marginBottom: '15px' }}>{editingExpenseId ? 'Edit Expense' : 'Record New Expense'}</h4>
          <form onSubmit={submitExpense} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select Category</label>
              <select 
                name="categoryId" 
                value={expenseFormData.categoryId} 
                onChange={handleExpenseChange} 
                required 
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">-- Select Category --</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Amount</label>
              <input 
                type="number" 
                name="amount" 
                value={expenseFormData.amount} 
                onChange={handleExpenseChange} 
                required 
                min="0"
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Bank Name (for deduction)</label>
              <select 
                name="bankAccountId" 
                value={expenseFormData.bankAccountId} 
                onChange={handleExpenseChange} 
                required 
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">-- Select Bank Name --</option>
                {bankAccounts.map(bank => (
                  <option key={bank._id} value={bank._id}>{bank.bankName} - {bank.accountName}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Payment Method</label>
              <select 
                name="paymentMethod" 
                value={expenseFormData.paymentMethod} 
                onChange={handleExpenseChange} 
                required 
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">-- Select Payment Method --</option>
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="UPI">UPI</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Date</label>
              <input 
                type="text"
                disabled
                value={new Date().toLocaleString()} 
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#f3f4f6' }}
              />
              <small style={{ color: '#666' }}>Date and time will be auto-generated.</small>
            </div>
            
            <div style={{ gridColumn: 'span 2', textAlign: 'right' }}>
              <button 
                type="submit" 
                disabled={expenseLoading}
                style={{ 
                  padding: '10px 20px', 
                  backgroundColor: '#003e6d', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {expenseLoading ? 'Saving...' : editingExpenseId ? 'Update Expense' : 'Save Expense'}
              </button>
            </div>
          </form>
        </div>
      )}

      {!showExpenseForm && (
        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '40px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px' }}>Date</th>
                <th style={{ padding: '12px' }}>Category</th>
                <th style={{ padding: '12px' }}>Bank Name</th>
                <th style={{ padding: '12px' }}>Payment Method</th>
                <th style={{ padding: '12px' }}>Amount</th>
                <th style={{ padding: '12px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length > 0 ? (
                expenses.map(expense => (
                  <tr key={expense._id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{new Date(expense.date).toLocaleString()}</td>
                    <td style={{ padding: '12px' }}>{expense.categoryId?.name || '-'}</td>
                    <td style={{ padding: '12px' }}>{expense.bankAccountId?.bankName || '-'}</td>
                    <td style={{ padding: '12px' }}>{expense.paymentMethod || '-'}</td>
                    <td style={{ padding: '12px', color: '#dc3545', fontWeight: 'bold' }}>-₹{expense.amount}</td>
                    <td style={{ padding: '12px', display: 'flex', gap: '10px' }}>
                      <button 
                        onClick={() => handleEdit(expense)} 
                        style={{ background: '#ffc107', color: '#000', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        Edit
                      </button>
                      <button 
                        onClick={() => deleteExpense(expense._id)} 
                        style={{ background: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ padding: '20px', textAlign: 'center' }}>No expenses recorded yet.</td>
                </tr>
              )}
            </tbody>
          </table>
          {expenses.length > 0 && (
            <div style={{ padding: '20px', borderTop: '2px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Total Expenses:</h3>
              <h3 style={{ margin: 0, color: '#dc3545' }}>₹{calculateTotal()}</h3>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Expenses;
